import { useBlockNumber } from 'wagmi';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { EventFilter, keyToFilter } from '../../utils/logParsing';
import { fetchedLogs, fetchedLogsError, fetchingLogs } from '../slices/logs';
import { useEthersProvider } from '../../hooks/useEthersProvider';

const Updater = (): null => {
  const dispatch = useAppDispatch();
  const state = useAppSelector(state => state.logs);
  const provider = useEthersProvider();

  const { data: blockNumber } = useBlockNumber();
  const blockNum = blockNumber ? Number(blockNumber) : undefined;

  const filtersNeedFetch: EventFilter[] = useMemo(() => {
    if (typeof blockNum !== 'number') return [];

    return Object.keys(state)
      .filter(key => {
        const { fetchingBlockNumber, results, listeners } = state[key];
        if (listeners === 0) return false;
        if (typeof fetchingBlockNumber === 'number' && fetchingBlockNumber >= blockNum)
          return false;
        if (
          results &&
          typeof results.blockNumber === 'number' &&
          results.blockNumber >= blockNum
        )
          return false;
        return true;
      })
      .map(key => keyToFilter(key));
  }, [blockNum, state]);

  useEffect(() => {
    if (!provider || typeof blockNum !== 'number' || filtersNeedFetch.length === 0) return;

    dispatch(fetchingLogs({ filters: filtersNeedFetch, blockNumber: blockNum }));
    filtersNeedFetch.forEach(filter => {
      provider
        .getLogs({
          ...filter,
          fromBlock: 0,
          toBlock: blockNum,
        })
        .then(logs => {
          dispatch(
            fetchedLogs({
              filter,
              results: { logs, blockNumber: blockNum },
            }),
          );
        })
        .catch(error => {
          console.error('Failed to get logs', filter, error);
          dispatch(
            fetchedLogsError({
              filter,
              blockNumber: blockNum,
            }),
          );
        });
    });
  }, [blockNum, dispatch, filtersNeedFetch, provider]);

  return null;
};

export default Updater;
